import type { MatchGroup, MatchGroupLine } from '../../../hooks/useMatchGroups';

/**
 * Tokenize a product name down to a list of normalized tokens. Strips
 * whitespace/punctuation, collapses ``*`` / ``×`` → ``x`` so that
 * "Erdbeeren 10*500g" and "Erdbeeren 10x500g" agree on the package
 * spec token. Returns tokens of length ≥ 2 (drops lone digits and the
 * stray ``g``/``x`` separators) so the first token is a real product
 * word, not a stripped artifact.
 */
function normalizeTokens(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[*x×]/g, 'x')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter((t) => t.length >= 2);
}

/**
 * Produce tiered lookup keys from a product name — from most specific
 * to least specific. The price index is built across all tiers, and
 * lookup walks the tiers in order so an orphan with a unique packaging
 * spec still falls back to the same-family price.
 *
 * Example for "Erdbeeren 10x500g IFCO Black Lift Lock":
 *   tier 0 (specific): "erdbeeren 10x500g ifco"
 *   tier 1           : "erdbeeren 10x500g"
 *   tier 2 (loose)   : "erdbeeren"
 */
export function productLookupTiers(name: string): string[] {
  const tokens = normalizeTokens(name);
  if (tokens.length === 0) return [];
  const tiers = new Set<string>();
  for (let n = Math.min(3, tokens.length); n >= 1; n--) {
    tiers.add(tokens.slice(0, n).join(' '));
  }
  return Array.from(tiers);
}

export interface PriceIndex {
  /** All observed prices keyed by every lookup tier. The same line
   *  contributes a price to *each* of its tier keys, so fuzzy lookup
   *  naturally averages across siblings of the same product family
   *  without losing the exact-key win when present. */
  byTier: Map<string, number[]>;
}

/**
 * Build a product → unit-price index from every settlement line we can
 * see. Keys by the LS-side product name when paired (so the orphan
 * LS lookup hits a delivery-perspective name) and by the settlement's
 * own product name when the GS is itself an orphan. Indexes every
 * tier (specific → loose) so a unique-packaging orphan LS still finds
 * a same-family price.
 *
 * Why source from orphan settlements too: when a month contains no
 * paired groups at all, the previous "paired-only" index built
 * nothing and every orphan LS rendered "kein Preisanker". Settlement
 * lines carry the same authoritative unit_price whether they're paired
 * or not — using them as a fallback closes that gap without losing
 * the paired-evidence preference (paired contributions land in the
 * same bucket; lookup averages across the bucket so loose anchors
 * don't dominate when tight evidence exists).
 */
export function buildPriceIndex(groups: MatchGroup[]): PriceIndex {
  const byTier = new Map<string, number[]>();
  const push = (productName: string, price: number) => {
    if (!Number.isFinite(price) || price <= 0) return;
    for (const key of productLookupTiers(productName)) {
      const bucket = byTier.get(key) ?? [];
      bucket.push(price);
      byTier.set(key, bucket);
    }
  };

  // Settlement lines authoritatively carry money. When only ``amount``
  // is populated (no per-unit price extracted), ``amount / quantity``
  // recovers the implicit per-unit figure so a month-where-only-line-
  // amounts-survived still seeds the index instead of rendering
  // "kein Preisanker" everywhere.
  const readUnitPrice = (sl: MatchGroupLine): number | null => {
    if (sl.unitPrice != null && Number.isFinite(sl.unitPrice) && sl.unitPrice > 0) {
      return sl.unitPrice;
    }
    if (
      sl.amount != null &&
      Number.isFinite(sl.amount) &&
      sl.quantity != null &&
      Number.isFinite(sl.quantity) &&
      sl.quantity > 0
    ) {
      return sl.amount / sl.quantity;
    }
    return null;
  };

  for (const g of groups) {
    // Tier A: paired groups — best evidence. Key by the LS-side name
    // because that's the side the orphan-LS lookup will use.
    // #1291 N:1 fan-out — every paired settlement line contributes its
    // own sample (pre-#1291 the singular walk dropped all but the
    // first pair). Negative correction lines are naturally excluded:
    // ``readUnitPrice`` rejects non-positive unit prices and may only
    // return a negative implicit ``amount / quantity`` figure, which
    // ``push`` then drops via its ``price <= 0`` guard — the index
    // never holds a non-positive anchor.
    if (g.deliveryEvent && g.settlementEvent) {
      const seLineById = new Map<string, MatchGroupLine>();
      for (const sl of g.settlementLines) seLineById.set(sl.lineId, sl);
      for (const dl of g.deliveryLines) {
        for (const sid of dl.matchedLineIds) {
          const sl = seLineById.get(sid);
          if (!sl) continue;
          const price = readUnitPrice(sl);
          if (price == null) continue;
          push(dl.productName, price);
        }
      }
      continue;
    }
    // Tier B: orphan settlements — fallback evidence when no paired
    // anchor exists in the month.
    if (!g.deliveryEvent && g.settlementEvent) {
      for (const sl of g.settlementLines) {
        const price = readUnitPrice(sl);
        if (price == null) continue;
        push(sl.productName, price);
      }
    }
  }
  return { byTier };
}

/**
 * Look up the unit price for a product name. Walks the lookup tiers
 * from specific to loose and returns the first tier with evidence;
 * multiple prices at the same tier are averaged. Returns ``null``
 * when no tier matches.
 */
export function lookupPrice(index: PriceIndex, productName: string): number | null {
  for (const key of productLookupTiers(productName)) {
    const bucket = index.byTier.get(key);
    if (bucket && bucket.length > 0) {
      const sum = bucket.reduce((a, b) => a + b, 0);
      return sum / bucket.length;
    }
  }
  return null;
}

/**
 * Estimate the total monetary value of an orphan-delivery group — the
 * sum of (quantity × per-tier-price) across its delivery lines. Used
 * by both the dashboard tile (aggregated across all orphan deliveries)
 * and the canvas missing-side stub (single-group estimate per tile).
 * Returns ``null`` when the group has no quantities or no line found
 * any price anchor.
 */
export function estimateOrphanDeliveryValue(
  group: MatchGroup,
  index: PriceIndex,
): number | null {
  if (group.status !== 'orphan_delivery') return null;
  let estimated = 0;
  let covered = 0;
  for (const dl of group.deliveryLines) {
    if (dl.quantity == null) continue;
    const price = lookupPrice(index, dl.productName);
    if (price == null) continue;
    estimated += dl.quantity * price;
    covered++;
  }
  return covered > 0 ? estimated : null;
}
